import type { WidgetViewMode } from "#app/widgets/dashboards/use-widget-view";
import {
  isSharedTier,
  type WidgetViewTier,
} from "#app/widgets/dashboards/widget-view-policy";

/**
 * Tier + fork state → store mode, resolved against the viewer's runtime
 * state. Pure so the node harness can truth-table it (the feature's
 * pure-module rule). Which board a viewer sees is DECIDED BY STATE, never a
 * preference: no fork → the shared board; fork → their fork. ONE rule for
 * every view:
 *
 * - `private-owner` (My Work) edits its own board directly — plain
 *   write-through autosave; the view already IS personal, so no fork.
 * - The `acl` tier (a workspace dashboard) is a shared view whose write
 *   right comes from its row; it follows every shared rule below.
 * - A personal-only shared surface always uses the viewer's private fork.
 *   Its canonical board supplies defaults but never receives user edits.
 * - A shared view's non-writer is ALWAYS in `personal` mode: forkless they
 *   see the shared board read-through (the store seeds from canonical rows),
 *   and their first edit's debounced snapshot CREATES the fork — no
 *   permission wall, no arming step.
 * - A shared writer's default job is the shared board (`staged`, behind the
 *   toolbar's Save). Once they hold a fork they live on it (`personal`) —
 *   except while `editingTeamLayout`, the temporary jump back to the shared
 *   board ("Edit team layout" → Done).
 * - A consume-only viewer (share-only role on a shared board) never forks:
 *   they read the canonical rows in `staged` mode with no writer behind it —
 *   the host's `canEdit` is false, so the working copy is never touched and
 *   the store never reads or writes a fork.
 */
export function resolveWidgetViewMode(input: {
  tier: WidgetViewTier;
  canWrite: boolean;
  hasFork: boolean;
  editingTeamLayout: boolean;
  personalOnly?: boolean;
  consumeOnly?: boolean;
}): WidgetViewMode {
  if (isSharedTier(input.tier) && input.consumeOnly) return "staged";
  if (isSharedTier(input.tier) && input.personalOnly) return "personal";
  if (input.tier === "private-owner") return "autosave";
  if (!input.canWrite) return "personal";
  return input.hasFork && !input.editingTeamLayout ? "personal" : "staged";
}
